import React, { useState, useRef, Fragment } from "react";
import {
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Alert,
  Grid,
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { updateLayout } from "../../../services/Layouts";

export default function JsonForm(props) {
  const [showCloseWithoutSaveMessage, setShowCloseWithoutSaveMessage] = useState(false);
  const [layoutId, setLayoutId] = useState(props.layoutId);
  const [layoutName, setLayoutName] = useState(props.layoutName);
  const [layoutIcon, setLayoutIcon] = useState(props.layoutIcon);
  const [tempRegisterId, setTempRegisterId] = useState("");
  const [tempRegisterName, setTempRegisterName] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [layoutRegisters, setLayoutRegisters] = useState(props.layoutRegisters);
  const [saveButtomEnabled, setSaveButtomEnabled] = useState(false);
  const [error, setError] = useState("");
  const [enabledApplyButtons, setEnabledApplyButtons] = useState([]);
  const { t } = useTranslation();

  const addRegister = () => {
    if (!tempRegisterId || !tempRegisterName) {
      setError("Chave do Registro e Nome são obrigatórios!"); /*trad */
      return;
    }
    if (layoutRegisters[tempRegisterId]) {
      setError(`Chave do Registro ${tempRegisterId} já existe!`); /*trad */
      return;
    }
    setError("");
    setSaveButtomEnabled(true);
    setLayoutRegisters((prev) => ({
      ...prev,
      [tempRegisterId]: { name: tempRegisterName, fields: [] },
    }));
    setTempRegisterId("");
    setTempRegisterName("");
  };

  const addField = (regId) => {
    setError("");
    setSaveButtomEnabled(true);
    setLayoutRegisters((prev) => {
      const currentFields = prev[regId]?.fields ?? [];
      const updatedFields = [
        ...currentFields,
        {
          name: "Campo " + (currentFields.length + 1) /*trad */,
          index: currentFields.length + 1,
          size: 1,
        },
      ];
      return { ...prev, [regId]: { ...prev[regId], fields: updatedFields } };
    });
  };

  const updateField = (regId, fieldIndex, key, value) => {
    setError("");
    setSaveButtomEnabled(true);
    setLayoutRegisters((prev) => {
      const updatedFields = [...prev[regId].fields];
      updatedFields[fieldIndex][key] = value;
      return { ...prev, [regId]: { ...prev[regId], fields: updatedFields } };
    });
  };

  const removeField = (regId, fieldIndex) => {
    setError("");
    setSaveButtomEnabled(true);
    setLayoutRegisters((prev) => {
      const updatedFields = prev[regId].fields.filter((_, index) => index !== fieldIndex);
      return { ...prev, [regId]: { ...prev[regId], fields: updatedFields } };
    });
  };

  const editRegister = (oldKey, newKey, name) => {
    if (!newKey || !name) {
      setError("Chave do Registro e Nome são obrigatórios!"); /*trad */
      return;
    }

    setLayoutRegisters((prev) => ({
      ...prev,
      [oldKey]: { ...prev[oldKey], name: name },
    }));

    if (newKey && oldKey !== newKey) {
      setLayoutRegisters((prev) => {
        if (prev[newKey]) {
          setError(`Chave do Registro ${newKey} já existe!`); /*trad */
          return prev;
        }

        const updated = { ...prev };
        updated[newKey] = updated[oldKey];
        delete updated[oldKey];

        return updated;
      });
    }

    setError("");
    setSaveButtomEnabled(true);
  };

  const removeRegister = (key) => {
    setLayoutRegisters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    setError("");
    setSaveButtomEnabled(true);
  };

  const handleSave = () => {
    const output = {
      name: layoutName,
      icon: layoutIcon,
      registers: {
        ...layoutRegisters,
      },
    };
    let layout = JSON.stringify(output);
    handleUpdateLayout(layout);
  };

  const handleUpdateLayout = (content) => {
    updateLayout({
      layoutId,
      name: layoutName,
      icon: layoutIcon,
      content: content,
    })
      .then(() => {
        props.getLayouts();
        props.setOpenEditDialog(false);
      })
      .catch(() => {
        setError("Erro ao salvar layout"); /*trad */
      });
  };

  const handleEnableApplyButton = (btnId) => {
    if (enabledApplyButtons.includes(btnId)) {
      return;
    } else {
      setEnabledApplyButtons((prev) => [...prev, btnId]);
    }
  };
  const handleDisableApplyButton = (btnId) => {
    if (enabledApplyButtons.includes(btnId)) {
      setEnabledApplyButtons((prev) => prev.filter((id) => id !== btnId));
    }
  };

  function validateFields(fields) {
    const sorted = [...fields].sort((a, b) => a.index - b.index);
    const conflicts = [];

    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];

      const currentEnd = current.index + current.size - 1;

      if (next.index <= currentEnd) {
        conflicts.push({
          fieldA: current,
          fieldB: next,
        });
      }
    }

    return conflicts;
  }

  const getFieldKey = (regId, field) => `${regId}_${field.name}_${field.index}_${field.size}`;
  const refs = useRef([]);

  return (
    <>
      <DialogTitle>Editar Layout</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label={t("layoutName")}
                value={layoutName}
                onChange={(e) => {
                  setLayoutName(e.target.value);
                  setSaveButtomEnabled(true);
                }}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={7}>
              <TextField
                label="URL Ícone"
                value={layoutIcon}
                onChange={(e) => {
                  setLayoutIcon(e.target.value);
                  setSaveButtomEnabled(true);
                }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={1}>
              {layoutIcon && <img src={layoutIcon} style={{ width: "40px", maxHeight: "40px" }} />}
            </Grid>
          </Grid>
        </Box>

        {error && (
          <Box mt={2}>
            <Alert variant="outlined" severity="error">
              {error}
            </Alert>
          </Box>
        )}

        <Box mt={2}>
          {Object.keys(layoutRegisters).map((regId) => (
            <Accordion
              key={regId}
              expanded={expanded === regId}
              onChange={() => setExpanded(expanded === regId ? null : regId)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <IconButton
                  color="error"
                  onClick={() => removeRegister(regId)}
                  sx={{ position: "absolute", marginLeft: -1, marginTop: -1 }}
                >
                  <DeleteIcon />
                </IconButton>
                <Typography sx={{ marginLeft: 5 }}>{layoutRegisters[regId].name}</Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={2}>
                    <TextField
                      label="Chave do Registro"
                      fullWidth
                      defaultValue={regId}
                      inputRef={(e) => (refs.current[`${regId}_key`] = e)}
                      size="small"
                      onChange={() => handleEnableApplyButton(regId)}
                    />
                  </Grid>

                  <Grid item xs={9}>
                    <TextField
                      label="Nome do Registro"
                      fullWidth
                      defaultValue={layoutRegisters[regId].name}
                      inputRef={(e) => (refs.current[`${regId}_name`] = e)}
                      size="small"
                      onChange={() => handleEnableApplyButton(regId)}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      color="info"
                      onClick={() => {
                        editRegister(
                          regId,
                          refs.current[`${regId}_key`].value,
                          refs.current[`${regId}_name`].value,
                        );
                        handleDisableApplyButton(regId);
                      }}
                      disabled={!enabledApplyButtons.includes(regId)}
                    >
                      <CheckIcon />
                    </IconButton>
                  </Grid>

                  <Grid item xs={12}>
                    <Box mt={2} mb={2}>
                      <Divider>
                        <Chip label="Campos" size="small" />
                      </Divider>
                    </Box>
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <Grid container spacing={2}>
                    {expanded === regId &&
                      layoutRegisters[regId].fields?.map((field, fieldIndex) => (
                        <Fragment key={getFieldKey(regId, field)}>
                          <Grid item xs={9}>
                            <IconButton onClick={() => removeField(regId, fieldIndex)}>
                              <DeleteIcon />
                            </IconButton>

                            <TextField
                              label="Nome do Campo" /*trad */
                              defaultValue={field.name}
                              inputRef={(e) => (refs.current[`${regId}_${fieldIndex}_name`] = e)}
                              size="small"
                              sx={{ width: "calc(100% - 50px)", marginLeft: 1 }}
                              onChange={() => handleEnableApplyButton(getFieldKey(regId, field))}
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <TextField
                              label="Index"
                              type="number"
                              defaultValue={field.index}
                              inputRef={(e) => (refs.current[`${regId}_${fieldIndex}_index`] = e)}
                              size="small"
                              onChange={() => handleEnableApplyButton(getFieldKey(regId, field))}
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <TextField
                              label="Size"
                              type="number"
                              defaultValue={field.size}
                              inputRef={(e) => (refs.current[`${regId}_${fieldIndex}_size`] = e)}
                              size="small"
                              onChange={() => handleEnableApplyButton(getFieldKey(regId, field))}
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <IconButton
                              onClick={() => {
                                updateField(
                                  regId,
                                  fieldIndex,
                                  "name",
                                  refs.current[`${regId}_${fieldIndex}_name`].value,
                                );
                                updateField(
                                  regId,
                                  fieldIndex,
                                  "index",
                                  Number(refs.current[`${regId}_${fieldIndex}_index`].value),
                                );
                                updateField(
                                  regId,
                                  fieldIndex,
                                  "size",
                                  Number(refs.current[`${regId}_${fieldIndex}_size`].value),
                                );
                                handleDisableApplyButton(getFieldKey(regId, field));
                              }}
                              color="info"
                              disabled={!enabledApplyButtons.includes(getFieldKey(regId, field))}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Grid>
                        </Fragment>
                      ))}
                  </Grid>
                </Box>
                <Box mt={2}>
                  <Button
                    variant="contained"
                    onClick={() => addField(regId)}
                    startIcon={<AddIcon />}
                  >
                    Add Campo{/* trad */}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box mt={2} mb={2}>
                <Divider>
                  <Chip label="Novo Registro" size="small" />
                </Divider>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <TextField
                label="Chave do Registro"
                value={tempRegisterId}
                onChange={(e) => setTempRegisterId(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                label="Register Name"
                value={tempRegisterName}
                onChange={(e) => setTempRegisterName(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              <Button variant="contained" onClick={addRegister} startIcon={<AddIcon />}>
                Add Registro{/* trad */}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          type="submit"
          variant="contained"
          color="success"
          startIcon={<SaveIcon />}
          onClick={() => handleSave()}
          disabled={!saveButtomEnabled}
          sx={{ float: "left" }}
        >
          Salvar {/* trad */}
        </Button>

        {showCloseWithoutSaveMessage ? (
          <Box>
            <Typography variant="caption" color={"error"}>
              Você tem alterações não salvas. Deseja fechar sem salvar?{/* trad */}
            </Typography>
            <Button
              onClick={() => props.setOpenEditDialog(false)}
              variant="contained"
              sx={{ marginLeft: 1 }}
            >
              Fechar{/* trad */}
            </Button>
          </Box>
        ) : (
          <>
            {saveButtomEnabled ? (
              <Button onClick={() => setShowCloseWithoutSaveMessage(true)}>
                Fechar{/* trad */}
              </Button>
            ) : (
              <Button onClick={() => props.setOpenEditDialog(false)} variant="contained">
                Fechar{/* trad */}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </>
  );
}
